import type {
  AccentColour,
  ConsultLanguage,
  OnboardingRequest,
  PlanLine,
  PublicPlan,
  ShootType,
  Specialty,
  Weekday,
} from "@brandex/api-client";
import type { MessageKey } from "@brandex/i18n";
import { parseLocation, type LatLng } from "./location";

/** Onboarding steps, in order (prototype + account step first). */
export const STEPS = ["account", "practice", "qualifications", "chambers", "package", "brand"] as const;
export type Step = (typeof STEPS)[number];

export const SPECIALTIES: readonly Specialty[] = [
  "gynaecology_obstetrics",
  "paediatrics",
  "medicine",
  "cardiology",
  "dermatology",
  "orthopaedics",
  "other",
];
export const LANGUAGES: readonly ConsultLanguage[] = ["bangla", "english", "hindi", "arabic"];
/** Bangladeshi week order, Saturday first. */
export const WEEKDAYS: readonly Weekday[] = ["sat", "sun", "mon", "tue", "wed", "thu", "fri"];
export const SLOT_MINUTES = [10, 15, 20, 30, 45] as const;
export const SHOOT_TYPES: readonly ShootType[] = ["in_studio", "in_person", "podcast"];

export interface DegreeDraft {
  title: string;
  institution: string;
  /** Free text so a half-typed year survives; validated on submit. */
  year: string;
}

export interface ChamberDraft {
  /** Set for saved chambers (profile page); bookings refer to it. */
  id?: string;
  name: string;
  address: string;
  days: Weekday[];
  opens: string;
  closes: string;
  slotMinutes: number;
  /** Map position as typed or pasted (coordinates or a Google Maps link); optional. */
  location: string;
}

export type PackageDraft =
  | { kind: "plan"; planId: string }
  | { kind: "custom"; quantities: Record<string, number>; addOns: string[]; note: string }
  | { kind: "none" };

/** Everything the wizard collects. The password is never part of the draft. */
export interface OnboardingDraft {
  fullName: string;
  email: string;
  phone: string;
  specialty: Specialty | null;
  designation: string;
  registrationNo: string;
  languages: ConsultLanguage[];
  degrees: DegreeDraft[];
  bio: string;
  chambers: ChamberDraft[];
  autoConfirm: boolean;
  package: PackageDraft;
  accent: AccentColour;
  shootType: ShootType;
}

export const emptyChamber = (): ChamberDraft => ({
  name: "",
  address: "",
  days: [],
  opens: "17:00",
  closes: "20:00",
  slotMinutes: 15,
  location: "",
});

/** The chamber's map position to send, or null when none (or not readable). */
export function chamberLocation(c: Pick<ChamberDraft, "location">): LatLng | null {
  const where = parseLocation(c.location);
  return where.kind === "ok" ? where.point : null;
}

export const emptyDraft = (): OnboardingDraft => ({
  fullName: "",
  email: "",
  phone: "",
  specialty: null,
  designation: "",
  registrationNo: "",
  languages: ["bangla"],
  degrees: [{ title: "", institution: "", year: "" }],
  bio: "",
  chambers: [emptyChamber()],
  autoConfirm: true,
  package: { kind: "none" },
  accent: "azure",
  shootType: "in_studio",
});

export const DRAFT_STORAGE_KEY = "bx_onboarding_draft";

/** Fills empty account fields from the signed-in user (draft values win). */
export function withAccount(
  d: OnboardingDraft,
  user: { display_name: string; email: string; phone?: string | null } | undefined,
): OnboardingDraft {
  if (user === undefined) return d;
  return {
    ...d,
    fullName: d.fullName.trim() === "" ? user.display_name : d.fullName,
    email: d.email.trim() === "" ? user.email : d.email,
    phone: d.phone.trim() === "" ? (user.phone ?? "") : d.phone,
  };
}

/** Restores a saved draft; anything unreadable falls back to empty. */
export function loadDraft(storage: Pick<Storage, "getItem"> | undefined): OnboardingDraft {
  try {
    const raw = storage?.getItem(DRAFT_STORAGE_KEY);
    if (raw === null || raw === undefined) return emptyDraft();
    const parsed = JSON.parse(raw) as Partial<OnboardingDraft>;
    const draft = { ...emptyDraft(), ...parsed };
    // Chambers saved before a field existed get its default (e.g. `location`).
    return { ...draft, chambers: draft.chambers.map((c) => ({ ...emptyChamber(), ...c })) };
  } catch {
    return emptyDraft();
  }
}

export function saveDraft(storage: Pick<Storage, "setItem"> | undefined, draft: OnboardingDraft): void {
  try {
    storage?.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Private mode or quota: progress simply isn't kept.
  }
}

export function clearDraft(storage: Pick<Storage, "removeItem"> | undefined): void {
  try {
    storage?.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // Nothing to clean up.
  }
}

/** Field → message key; empty when the step is valid. */
export type StepErrors = Record<string, MessageKey>;

const minutes = (hhmm: string): number | null => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (m === null) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 23 && min <= 59 ? h * 60 + min : null;
};

/** Mirrors the server rule: a four-digit year from 1950 to next year. */
export function validDegreeYear(raw: string, now: Date = new Date()): boolean {
  if (!/^\d{4}$/.test(raw)) return false;
  const y = Number(raw);
  return y >= 1950 && y <= now.getFullYear() + 1;
}

/** Mirrors `PhoneNumber::parse_national` loosely; the server is authoritative. */
export function looksLikePhone(raw: string): boolean {
  const digits = raw.replace(/[\s\-().]/g, "");
  return /^(\+[1-9]\d{7,14}|0\d{9,13}|00[1-9]\d{7,14})$/.test(digits);
}

/** `password: null` skips the password rule (staff onboarding: the doctor sets it later). */
export function validateStep(step: Step, d: OnboardingDraft, password: string | null = ""): StepErrors {
  const e: StepErrors = {};
  const required = (field: string, value: string) => {
    if (value.trim() === "") e[field] = "onboarding.error.required";
  };
  switch (step) {
    case "account":
      required("full_name", d.fullName);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) e.email = "onboarding.error.email";
      if (d.phone.trim() === "") e.phone = "onboarding.error.required";
      else if (!looksLikePhone(d.phone)) e.phone = "onboarding.error.phone";
      if (password !== null && password.length < 12) e.password = "auth.passwordHint";
      break;
    case "practice":
      // Signed-in doctors skip the account step, so the name is checked here too.
      required("full_name", d.fullName);
      if (d.specialty === null) e.specialty = "onboarding.error.required";
      if (d.designation.trim().length < 2) e.designation = "onboarding.error.required";
      if (d.languages.length === 0) e.languages = "onboarding.error.languages";
      break;
    case "qualifications":
      d.degrees.forEach((g, i) => {
        if (g.title.trim().length < 2) e[`degrees.${i}.title`] = "onboarding.error.required";
        const year = g.year.trim();
        if (year !== "" && !validDegreeYear(year)) e[`degrees.${i}.year`] = "onboarding.error.year";
      });
      if (d.degrees.length === 0) e["degrees.0.title"] = "onboarding.error.required";
      if (d.bio.trim().length < 20 || d.bio.trim().length > 600) e.bio = "onboarding.error.bio";
      break;
    case "chambers":
      d.chambers.forEach((c, i) => {
        if (c.name.trim().length < 2) e[`chambers.${i}.name`] = "onboarding.error.required";
        if (c.address.trim().length < 2) e[`chambers.${i}.address`] = "onboarding.error.required";
        if (c.days.length === 0) e[`chambers.${i}.days`] = "onboarding.error.days";
        const open = minutes(c.opens);
        const close = minutes(c.closes);
        if (open === null || close === null || close < open + c.slotMinutes) e[`chambers.${i}.closes`] = "onboarding.error.hours";
        const where = parseLocation(c.location);
        if (where.kind === "short-link") e[`chambers.${i}.location`] = "onboarding.error.shortLink";
        else if (where.kind === "invalid") e[`chambers.${i}.location`] = "onboarding.error.location";
      });
      break;
    case "package":
      if (d.package.kind === "none") e.package = "onboarding.error.package";
      if (d.package.kind === "custom" && !Object.values(d.package.quantities).some((q) => q > 0) && d.package.addOns.length === 0) {
        e.package = "onboarding.error.custom";
      }
      break;
    case "brand":
      break;
  }
  return e;
}

/**
 * The draft field a server validation field refers to. The server names list
 * fields without an index (`degrees.title`), so the first entry is marked.
 */
export function draftField(field: string): string {
  if (field === "display_name") return "full_name";
  if (field === "degrees") return "degrees.0.title";
  if (field === "chambers") return "chambers.0.name";
  if (["plan_id", "items", "note", "custom_items"].includes(field)) return "package";
  const m = /^(degrees|chambers)\.([a-z_]+)$/.exec(field);
  if (m !== null) {
    const leaf = m[1] === "chambers" && (m[2] === "opens" || m[2] === "slot_minutes") ? "closes" : m[2];
    return `${m[1]}.0.${leaf}`;
  }
  return field;
}

/** The step a server validation field belongs to (for jumping back). */
export function stepForField(field: string | undefined): Step | null {
  if (field === undefined) return null;
  if (["email", "password", "display_name", "phone"].includes(field)) return "account";
  if (["full_name", "specialty", "designation", "registration_no", "languages"].includes(field)) return "practice";
  if (field.startsWith("degrees") || field === "bio") return "qualifications";
  if (field.startsWith("chambers")) return "chambers";
  if (["plan_id", "items", "note", "custom_items"].includes(field)) return "package";
  return null;
}

/**
 * Lines a custom package can list: per-item components (with a default
 * quantity from the first plan offering them) and monthly add-ons such as
 * the website, deduplicated across all plans on offer.
 */
export function customLines(plans: readonly PublicPlan[]): { perItem: PlanLine[]; addOns: PlanLine[] } {
  const seen = new Set<string>();
  const perItem: PlanLine[] = [];
  const addOns: PlanLine[] = [];
  for (const plan of plans) {
    for (const line of plan.lines) {
      if (seen.has(line.component_id) || line.always_included) continue;
      seen.add(line.component_id);
      (line.unit === "per_item" ? perItem : addOns).push(line);
    }
  }
  return { perItem, addOns };
}

/** Builds the `POST /v1/orgs/{id}/onboarding` body from a valid draft. */
export function toRequest(d: OnboardingDraft, plans: readonly PublicPlan[]): OnboardingRequest {
  const pkg: Pick<OnboardingRequest, "plan_id" | "custom_items" | "note"> =
    d.package.kind === "plan"
      ? { plan_id: d.package.planId, custom_items: null, note: "" }
      : d.package.kind === "custom"
        ? {
            plan_id: null,
            custom_items: [
              ...customLines(plans).perItem.map((l) => ({
                component_id: l.component_id,
                quantity: (d.package as Extract<PackageDraft, { kind: "custom" }>).quantities[l.component_id] ?? l.quantity,
              })),
              ...d.package.addOns.map((id) => ({ component_id: id, quantity: 1 })),
            ].filter((i) => i.quantity > 0),
            note: d.package.note.trim(),
          }
        : { plan_id: null, custom_items: null, note: "" };
  return {
    full_name: d.fullName.trim(),
    specialty: d.specialty ?? "other",
    designation: d.designation.trim(),
    registration_no: d.registrationNo.trim() === "" ? null : d.registrationNo.trim(),
    languages: d.languages,
    degrees: d.degrees.map((g) => ({
      title: g.title.trim(),
      institution: g.institution.trim(),
      year: validDegreeYear(g.year.trim()) ? Number(g.year.trim()) : null,
    })),
    bio: d.bio.trim(),
    chambers: d.chambers.map((c) => ({
      name: c.name.trim(),
      address: c.address.trim(),
      days: WEEKDAYS.filter((w) => c.days.includes(w)),
      opens: c.opens,
      closes: c.closes,
      slot_minutes: c.slotMinutes,
      location: chamberLocation(c),
    })),
    auto_confirm_bookings: d.autoConfirm,
    ...pkg,
    brand: { accent: d.accent, shoot_type: d.shootType },
  };
}
